/**
 * Title, description, canonical URL, social image and JSON-LD for each Learn hub page. The build writes
 * them into each page's static HTML (vite/academy-prerender.ts); the app sets the same tags as you
 * navigate (head.ts), so crawlers that run scripts see what crawlers that don't see. Loaded by the
 * Vite config too, hence the .ts import extensions.
 */
import { courseArticles, sortNewest } from './catalog.ts';
import { routePath, type LearnRoute } from './routes.ts';
import { categoryInfo, courseInfo, levelInfo, topicInfo } from './taxonomy.ts';
import type { ArticleMeta, Faq } from './types.ts';

export interface Head {
  title: string;
  description: string;
  /** Path of the canonical URL. */
  path: string;
  /** Path of the social image. */
  image: string;
  type: 'website' | 'article';
  jsonLd: object[];
  /** Pages that don't exist. */
  noindex?: boolean;
  publishedAt?: string;
  updatedAt?: string;
}

export interface SeoContext {
  articles: readonly ArticleMeta[];
  /** Absolute origin, e.g. https://app.example.com */
  origin: string;
  brand: string;
  /** Square logo path, for the publisher. */
  logo: string;
  /** The article's FAQs, when the page is an article and they are loaded. */
  faqs?: readonly Faq[];
}

export const DEFAULT_OG = '/academy/img/og/academy.jpg';
export const HOME_DESCRIPTION =
  'Free trading education from beginner to advanced: CFDs, crypto, leverage, risk management, technical analysis and step-by-step platform guides, with every example in USDT.';

const abs = (origin: string, path: string) => (/^https?:/.test(path) ? path : origin + path);

function breadcrumbs(origin: string, items: { name: string; path: string }[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: abs(origin, it.path) })),
  };
}

function itemList(origin: string, list: readonly ArticleMeta[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: list.map((a, i) => ({ '@type': 'ListItem', position: i + 1, url: abs(origin, routePath({ kind: 'article', slug: a.slug })), name: a.title })),
  };
}

const notFound = (path: string): Head => ({ title: 'Page not found', description: 'This Academy page does not exist.', path, image: DEFAULT_OG, type: 'website', jsonLd: [], noindex: true });

export function headFor(route: LearnRoute, ctx: SeoContext): Head {
  const { articles, origin, brand } = ctx;
  const site = `${brand} Academy`;
  const home = { name: 'Academy', path: '/academy' };
  const path = routePath(route);
  switch (route.kind) {
    case 'home':
      return {
        title: `${site}: Learn to Trade Crypto, Commodities and Stocks`,
        description: HOME_DESCRIPTION,
        path,
        image: DEFAULT_OG,
        type: 'website',
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: site,
            url: abs(origin, path),
            publisher: { '@type': 'Organization', name: brand, logo: { '@type': 'ImageObject', url: abs(origin, ctx.logo) } },
          },
        ],
      };
    case 'articles':
      return {
        title: `All Trading Articles | ${site}`,
        description: `Every ${brand} Academy article in one place. Filter by level, category and topic, or search for what you want to learn.`,
        path,
        image: DEFAULT_OG,
        type: 'website',
        jsonLd: [breadcrumbs(origin, [home, { name: 'Articles', path }]), itemList(origin, sortNewest(articles))],
      };
    case 'category': {
      const c = categoryInfo(route.id);
      if (!c) return notFound(path);
      const list = sortNewest(articles.filter((a) => a.category === c.id));
      return {
        title: `${c.title} Articles | ${site}`,
        description: `${c.blurb} ${list.length} articles from beginner to advanced.`,
        path,
        image: DEFAULT_OG,
        type: 'website',
        jsonLd: [breadcrumbs(origin, [home, { name: c.title, path }]), itemList(origin, list)],
      };
    }
    case 'topic': {
      const t = topicInfo(route.id);
      if (!t) return notFound(path);
      const list = sortNewest(articles.filter((a) => a.topics.includes(t.id)));
      return {
        title: `${t.title}: Guides and Articles | ${site}`,
        description: `${t.blurb} ${list.length} ${list.length === 1 ? 'article' : 'articles'} on ${t.title.toLowerCase()}.`,
        path,
        image: DEFAULT_OG,
        type: 'website',
        jsonLd: [breadcrumbs(origin, [home, { name: t.title, path }]), itemList(origin, list)],
      };
    }
    case 'course': {
      const c = courseInfo(route.id);
      if (!c) return notFound(path);
      const list = courseArticles(articles, c.id);
      return {
        title: `${c.title}: ${list.length} Free Lessons | ${site}`,
        description: c.intro,
        path,
        image: DEFAULT_OG,
        type: 'website',
        jsonLd: [breadcrumbs(origin, [home, { name: c.title, path }]), itemList(origin, list)],
      };
    }
    case 'article': {
      const a = articles.find((x) => x.slug === route.slug);
      if (!a) return notFound(path);
      const course = courseInfo(a.course)!;
      const jsonLd: object[] = [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: a.title.slice(0, 110),
          description: a.description,
          image: [abs(origin, a.og)],
          datePublished: a.publishedAt,
          dateModified: a.updatedAt,
          inLanguage: 'en',
          articleSection: course.title,
          educationalLevel: levelInfo(a.level).label,
          keywords: a.topics.map((t) => topicInfo(t)?.title ?? t).join(', '),
          wordCount: a.words,
          position: a.order,
          author: { '@type': 'Organization', name: brand, url: abs(origin, '/academy') },
          publisher: { '@type': 'Organization', name: brand, logo: { '@type': 'ImageObject', url: abs(origin, ctx.logo) } },
          mainEntityOfPage: { '@type': 'WebPage', '@id': abs(origin, path) },
        },
        breadcrumbs(origin, [home, { name: course.title, path: routePath({ kind: 'course', id: course.id }) }, { name: a.title, path }]),
      ];
      if (ctx.faqs?.length) {
        jsonLd.push({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: ctx.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        });
      }
      return { title: `${a.title} | ${site}`, description: a.description, path, image: a.og, type: 'article', jsonLd, publishedAt: a.publishedAt, updatedAt: a.updatedAt };
    }
  }
}

/** JSON for a <script type="application/ld+json">, safe to inline in HTML. */
export const jsonLdText = (o: object) => JSON.stringify(o).replace(/</g, '\\u003c');
